import React, { useState, useRef } from "react";
import Select from "react-select";
import { toast } from "react-toastify";
import { PrismAsync as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneLight } from "react-syntax-highlighter/dist/esm/styles/prism";
import { DIALECTS } from "types/relation";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import { Button } from "aible";
import styles from "components/Tiles/Sense/style.module.css";
import LoadingIndicator from "components/LoadingIndicator/LoadingIndicator";
import { AliasInput, Labeled, MappedCategoricalValueInput } from "components/Tiles/Sense/Relation/Common";
import { getDraftValidatedOutput } from "@/containers/Relation/reducer";
import { useDispatch, useSelector } from "@/types/store";
import MarkdownPanel from "@/components/ChatGPT/MarkdownPanel";
import { customMapper, selectOnChangeWrapper } from "@/utils/select";
import { keysOf } from "@/utils/collect";
import { getDataset, getDatasets } from "@/containers/Home/reducer";
import { getLatestTag } from "@/utils/relation";
const MARKDOWN_OVERRIDES = {
    code_block: {
        wrap: "soft"
    }
};
export const HiddenRawFilePicker = ({ innerRef, onUpdate }) => {
    const handleChange = (event) => {
        if (event.currentTarget === null || !("files" in event.currentTarget)) {
            return;
        }
        const files = event.currentTarget?.files ? event.currentTarget.files : undefined;
        onUpdate(files);
    };
    return (React.createElement("div", { style: { display: "none" } },
        React.createElement("input", { type: "file", id: "fUpload", ref: innerRef, onChange: (e) => {
                handleChange(e);
            } })));
};
const DIALECT_MAPPER = customMapper((d) => DIALECTS[d]);
const DATASET_MAPPER = customMapper((d) => `(${d.id}) ${d.name}`);
export const AliasedDatasetInput = ({ relation, aliasedDataset, onChange, isDisabled, onDelete }) => {
    const datasets = useSelector(getDatasets);
    const tag = getLatestTag(relation);
    const datasetId = tag?.dataset_id;
    const dataset = useSelector((state) => getDataset(state, datasetId));
    const validDatasets = keysOf(datasets)
        .map((key) => datasets[key])
        .filter((dataset) => dataset !== undefined)
        .filter((d) => d &&
        d.deleted !== true &&
        d.isHidden !== true &&
        d.id !== dataset?.id &&
        d.account_id == dataset?.account_id &&
        d.folder_id === dataset?.folder_id &&
        d.schema_monitor_id == dataset.schema_monitor_id);
    const selectedDataset = validDatasets.find((d) => d.id == aliasedDataset.dataset_id);
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { style: { flexGrow: 1, marginRight: "5px" } },
            React.createElement(Labeled, { isBold: true, label: "Dataset" },
                React.createElement(Select, { key: selectedDataset?.id, options: DATASET_MAPPER.mapAll(validDatasets), value: DATASET_MAPPER.map(selectedDataset), onChange: selectOnChangeWrapper((d) => {
                        if (d === undefined) {
                            onChange({ alias: "", dataset_id: -1 });
                        }
                        else {
                            onChange({ ...aliasedDataset, dataset_id: d.id });
                        }
                    }), placeholder: `Search or Select a Dataset`, isDisabled: isDisabled, isClearable: true }))),
        React.createElement("div", { className: styles.aliasInput },
            React.createElement(Labeled, { isBold: true, label: "Alias" },
                React.createElement(AliasInput, { value: aliasedDataset.alias, onChange: (alias) => onChange({
                        ...aliasedDataset,
                        alias: alias ?? ""
                    }), isDisabled: isDisabled || aliasedDataset.dataset_id < 0, placeholder: "Table Alias (Required)" })),
            onDelete && (React.createElement("div", { style: { marginLeft: "5px", display: "flex", alignItems: "end" } },
                React.createElement(Button, { type: "secondary", style: { padding: "7px", height: "38px", width: "38px" }, onClick: () => onDelete(), disabled: isDisabled },
                    React.createElement(FontAwesomeIcon, { icon: faTrash })))))));
};
export const AliasedDatasetMultiInput = ({ relation, onChange, isDisabled }) => {
    const handleChange = (update) => {
        onChange({ aliased_datasets: update });
    };
    return (React.createElement("div", null,
        React.createElement("div", null, relation.aliased_datasets?.map((aliasedDataset, index) => (React.createElement("div", { key: index, className: styles.tFlex, style: { justifyContent: "space-between", marginBottom: "10px" } },
            React.createElement(AliasedDatasetInput, { relation: relation, aliasedDataset: aliasedDataset, onChange: (update) => handleChange(relation.aliased_datasets?.map((x, i) => (i == index ? update : x))), onDelete: () => handleChange(relation.aliased_datasets?.filter((_, i) => i != index)), isDisabled: isDisabled }))))),
        React.createElement(Labeled, { isBold: true, label: "Add Aliased Dataset", style: { marginBottom: "10px" }, tooltip: "Add an alias that can be used to reference an existing Aible dataset in the custom SQL query." },
            React.createElement(Button, { type: "secondary", onClick: () => handleChange((relation.aliased_datasets ?? []).concat([
                    {
                        alias: "",
                        dataset_id: -1
                    }
                ])), style: { marginRight: "10px" }, disabled: isDisabled },
                React.createElement(FontAwesomeIcon, { icon: faPlus })))));
};
const SQLRelationInput = ({ relation, onChange, isDisabled }) => {
    const dispatch = useDispatch();
    const handleChange = (update) => onChange({ ...relation, ...update });
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState();
    const FileUploadRef = useRef(null);
    if (isLoading) {
        return React.createElement(LoadingIndicator, null);
    }
    const handleQueryUpdate = (query) => {
        setIsLoading(true);
        dispatch(getDraftValidatedOutput({
            ...relation,
            query: query
        }))
            .then((r) => {
            if (r.error) {
                setError(r.error);
            }
            handleChange({ output_schema: r.output_schema, query: query });
        })
            .finally(() => setIsLoading(false));
    };
    const importDisabled = isDisabled ||
        relation.dialect.length == 0 ||
        relation.virtual_table_name.length == 0 ||
        (relation.aliased_datasets !== undefined &&
            relation.aliased_datasets.length > 0 &&
            relation.aliased_datasets.some((x) => x.alias.length == 0 || x.dataset_id < 0));
    return (React.createElement("div", { style: { marginBottom: "20px", fontSize: "12px" } },
        (relation.query.length > 0 || error !== undefined) && (React.createElement("div", { style: { marginBottom: "10px" } },
            React.createElement(Button, { type: "secondary", style: { marginRight: "12px" }, onClick: () => {
                    setError(undefined);
                    handleChange({ ...relation, query: "" });
                } }, "Reset Query"))),
        React.createElement("div", { style: { display: relation.query.length == 0 ? undefined : "none" } },
            React.createElement(HiddenRawFilePicker, { innerRef: FileUploadRef, onUpdate: (files) => {
                    if (files === undefined) {
                        return;
                    }
                    for (const file of files) {
                        const fileReader = new FileReader();
                        fileReader.addEventListener("load", () => {
                            const result = fileReader.result?.toString();
                            if (result !== undefined) {
                                const parts = result.split(";");
                                const mimeType = parts[0].substring(5);
                                if (mimeType.startsWith("text/") ||
                                    mimeType == "application/sql" ||
                                    mimeType == "application/octet-stream") {
                                    handleQueryUpdate(atob(parts[1].substring(7)));
                                }
                                else {
                                    toast(`Unsupported File Type: ${mimeType}`, {
                                        type: toast.TYPE.ERROR,
                                        position: toast.POSITION.TOP_CENTER,
                                        autoClose: 3500
                                    });
                                }
                            }
                        });
                        fileReader.readAsDataURL(file);
                    }
                } }),
            React.createElement("div", null,
                React.createElement(Labeled, { isBold: true, label: "SQL Dialect", style: { marginBottom: "10px" } },
                    React.createElement(MappedCategoricalValueInput, { value: relation.dialect, values: keysOf(DIALECTS), onChange: (dialect) => handleChange({ dialect }), isDisabled: isDisabled, mapper: DIALECT_MAPPER, placeholder: "Select SQL Dialect", selectOnly: true }))),
            React.createElement(Labeled, { isBold: true, label: "Virtual Table Name", style: { marginBottom: "10px" }, tooltip: "The alias used in the custom SQL query to refer to this dataset after any pending updates have been performed." },
                React.createElement(AliasInput, { value: relation.virtual_table_name, onChange: (name) => handleChange({ virtual_table_name: name }), placeholder: "Table Alias (Required)", isDisabled: isDisabled })),
            React.createElement(AliasedDatasetMultiInput, { relation: relation, onChange: handleChange, isDisabled: isDisabled }),
            React.createElement(Labeled, { isBold: true, label: "Import SQL", style: { marginBottom: "10px" } },
                React.createElement("div", { style: { display: "flex" } },
                    React.createElement("div", null,
                        React.createElement(Button, { disabled: importDisabled, type: "secondary", style: { marginRight: "12px" }, onClick: () => FileUploadRef?.current?.click() }, "Upload File")),
                    React.createElement("div", null,
                        React.createElement(Button, { disabled: importDisabled, type: "secondary", style: { marginRight: "12px" }, onClick: () => navigator.clipboard.readText().then(handleQueryUpdate) }, "Read from Clipboard"))))),
        error !== undefined ? (React.createElement(MarkdownPanel, { overrides: MARKDOWN_OVERRIDES }, error)) : relation.query.length > 0 ? (React.createElement(Labeled, { isBold: true, label: "Custom Query", style: { marginBottom: "10px" } },
            React.createElement(SyntaxHighlighter, { language: "sql", style: oneLight }, relation.query))) : (React.createElement(React.Fragment, null))));
};
export default SQLRelationInput;
